import { useCallback, useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { type DiffData } from '@/stores/use-ui-store';
import { useUIStore } from '@/stores/use-ui-store';
import { useActiveEntityId, useNavigateToEntity } from '@/hooks/navigation';
import { useSnapshotService } from '@/lib/snapshot/use-snapshot-service';
import { useSyncSystem } from '@/lib/sync/sync-system-context';
import { Flex, Text, Button, IconButton, Popconfirm, message, Icon, Splitter, Empty } from '@nula/core-ui';
import { formatTimestamp } from '@/features/editor/components/history-panel';
import { EntityDocumentViewer } from '@/features/editor/components/rich-text-viewer';
import { EditorSkeleton } from '@/features/editor/components/editor-skeleton';
import { SnapshotViewer } from '@/app/routes/workspace/snapshot-viewer';
import { CanvasViewer } from '@/features/canvas/components/canvas-viewer';
import type { DiffRange, CanvasDiffResult } from '@/lib/diff/types';
import { computeDocumentDiff } from '@/features/editor/utils/document-diff-engine';
import { computeCanvasDiff } from '@/features/canvas/utils/canvas-diff-engine';
import styles from './diff-view.module.css';

// ── Pane label ────────────────────────────────────────────────────────────────

function PaneLabel({ children }: { children: React.ReactNode }) {
  return (
    <Flex align="center" className={styles.paneLabel}>
      <Text className={styles.paneLabelText}>
        {children}
      </Text>
    </Flex>
  );
}

// ── Scroll sync ───────────────────────────────────────────────────────────────

function useSyncedScroll() {
  const leftRef = useRef<HTMLElement | null>(null);
  const rightRef = useRef<HTMLElement | null>(null);
  const syncing = useRef(false);

  const attach = useCallback(() => {
    const left = leftRef.current;
    const right = rightRef.current;
    if (!left || !right) return;

    const onLeft = () => {
      if (syncing.current) return;
      syncing.current = true;
      right.scrollTop = left.scrollTop;
      syncing.current = false;
    };
    const onRight = () => {
      if (syncing.current) return;
      syncing.current = true;
      left.scrollTop = right.scrollTop;
      syncing.current = false;
    };

    left.addEventListener('scroll', onLeft, { passive: true });
    right.addEventListener('scroll', onRight, { passive: true });
    return () => {
      left.removeEventListener('scroll', onLeft);
      right.removeEventListener('scroll', onRight);
    };
  }, []);

  const detachRef = useRef<(() => void) | undefined>(undefined);

  const setLeftRef = useCallback((el: HTMLElement | null) => {
    leftRef.current = el;
    detachRef.current?.();
    detachRef.current = attach() ?? undefined;
  }, [attach]);

  const setRightRef = useCallback((el: HTMLElement | null) => {
    rightRef.current = el;
    detachRef.current?.();
    detachRef.current = attach() ?? undefined;
  }, [attach]);

  return { setLeftRef, setRightRef };
}

// ── Scroll wrapper ────────────────────────────────────────────────────────────

function ScrollPane({ children, scrollRef }: { children: React.ReactNode; scrollRef?: (el: HTMLElement | null) => void }) {
  return (
    <Flex ref={scrollRef} vertical className={styles.scrollPane}>
      {children}
    </Flex>
  );
}

// ── SnapshotPane — fetches/hydrates Y.Doc and runs diff computation ────────────

interface SnapshotPaneState {
  yDoc: Y.Doc | null;
  docRanges: DiffRange[];
  canvasResult: CanvasDiffResult | null;
  error: boolean;
}

interface SnapshotPaneProps {
  entityId: string;
  diffData: DiffData;
  currentDoc: Y.Doc | null;
  onReady: (state: SnapshotPaneState) => void;
  scrollRef?: (el: HTMLElement | null) => void;
  /** Forwarded to SnapshotViewer for canvas snapshots — frames content on mount. */
  fitOnMount?: boolean;
}

function SnapshotPane({ entityId, diffData, currentDoc, onReady, scrollRef, fitOnMount }: SnapshotPaneProps) {
  const service = useSnapshotService();
  const [state, setState] = useState<SnapshotPaneState>({
    yDoc: null,
    docRanges: [],
    canvasResult: null,
    error: false,
  });

  useEffect(() => {
    // Reset to loading state when diffData changes
    setState({ yDoc: null, docRanges: [], canvasResult: null, error: false });
    let cancelled = false;

    async function load() {
      try {
        let bytes: Uint8Array;
        if (diffData.mode === 'cleanup' && diffData.suggestedState) {
          bytes = diffData.suggestedState;
        } else {
          bytes = await service.fetchSnapshotState(entityId, diffData.snapshotId!);
        }
        if (cancelled) return;

        const snapshotDoc = new Y.Doc();
        Y.applyUpdateV2(snapshotDoc, bytes);

        let docRanges: DiffRange[] = [];
        let canvasResult: CanvasDiffResult | null = null;

        if (diffData.entityKind === 'canvas' && currentDoc) {
          canvasResult = computeCanvasDiff(currentDoc, snapshotDoc);
        } else if (currentDoc) {
          docRanges = computeDocumentDiff(currentDoc, snapshotDoc);
        }

        const nextState = { yDoc: snapshotDoc, docRanges, canvasResult, error: false };
        if (!cancelled) {
          setState(nextState);
          onReady(nextState);
        }
      } catch {
        if (!cancelled) setState((s) => ({ ...s, error: true }));
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [service, entityId, diffData, currentDoc, onReady]);

  if (state.error) {
    return (
      <Flex vertical align="center" justify="center" className={styles.snapshotErrorPane}>
        <Empty
          image={<Icon name="error" size={48} className={styles.errorIcon} />}
          description="Failed to load snapshot"
        />
      </Flex>
    );
  }

  if (!state.yDoc) {
    return <EditorSkeleton />;
  }

  const namespace = `snapshot-${entityId}-${diffData.snapshotId ?? diffData.timestamp}`;

  return (
    <ScrollPane scrollRef={scrollRef}>
      <SnapshotViewer
        yDoc={state.yDoc}
        namespace={namespace}
        entityKind={diffData.entityKind}
        diffRanges={state.docRanges.filter((r) => r.type === 'deleted')}
        canvasDiffResult={state.canvasResult ? { ...state.canvasResult, moved: new Set<string>() } : undefined}
        fitOnMount={fitOnMount}
      />
    </ScrollPane>
  );
}

// ── Main DiffView ─────────────────────────────────────────────────────────────

interface DiffViewProps {
  diffView: DiffData | null;
  onClose: () => void;
}

export function DiffView({ diffView, onClose }: DiffViewProps) {
  const service = useSnapshotService();
  const activeEntityId = useActiveEntityId();
  const bumpEditorRevision = useUIStore((s) => s.bumpEditorRevision);
  const navigateToEntity = useNavigateToEntity();
  const { registry } = useSyncSystem();
  const { setLeftRef, setRightRef } = useSyncedScroll();

  const [snapshotState, setSnapshotState] = useState<SnapshotPaneState>({
    yDoc: null,
    docRanges: [],
    canvasResult: null,
    error: false,
  });

  // Clear stale diff highlights when switching to a different snapshot
  useEffect(() => {
    setSnapshotState({ yDoc: null, docRanges: [], canvasResult: null, error: false });
  }, [diffView?.snapshotId]);

  const handleSnapshotReady = useCallback((state: SnapshotPaneState) => {
    setSnapshotState(state);
  }, []);

  if (!diffView || !activeEntityId) return null;

  const isCleanup = diffView.mode === 'cleanup';
  const entityKind = diffView.entityKind;
  const currentDoc = registry.getDoc(activeEntityId) ?? null;
  const currentAwareness = registry.getAwareness(activeEntityId) ?? null;

  const handleRestore = async () => {
    try {
      await service.restoreSnapshot(activeEntityId, diffView.snapshotId!);
      await registry.resetLocalState(activeEntityId, snapshotState.yDoc!);
      bumpEditorRevision();
      onClose();
      message.success('Version restored');
    } catch {
      message.error('Failed to restore version');
    }
  };

  const handleAcceptOrganize = async () => {
    if (!diffView.suggestedState) return;
    if (currentDoc) {
      Y.applyUpdate(currentDoc, diffView.suggestedState);
    }
    bumpEditorRevision();
    onClose();
    message.success('Note organised');
  };

  const handleDelete = async () => {
    onClose();
    try {
      await service.deleteOneSnapshot(activeEntityId, diffView.snapshotId!);
      message.success('Snapshot deleted');
    } catch {
      message.error('Failed to delete snapshot');
    }
  };

  const handleBackToList = () => {
    navigateToEntity(activeEntityId);
    useUIStore.getState().setVersionHistoryOpen(true);
  };

  // ── Left pane (current version) ──────────────────────────────────────────

  const leftPane = entityKind === 'document' ? (
    <ScrollPane scrollRef={setLeftRef}>
      <EntityDocumentViewer
        entityId={activeEntityId}
        diffRanges={snapshotState.docRanges.filter((r) => r.type === 'added')}
      />
    </ScrollPane>
  ) : entityKind === 'canvas' && currentDoc && currentAwareness ? (
    <CanvasViewer
      yDoc={currentDoc}
      awareness={currentAwareness}
      entityId={activeEntityId}
      diffResult={snapshotState.canvasResult ?? undefined}
      fitOnMount
    />
  ) : (
    <Flex vertical align="center" justify="center" className={styles.loadingFallback}>
      <Text size="sm" type="secondary">Loading…</Text>
    </Flex>
  );

  // ── Right pane (snapshot / suggestion) ───────────────────────────────────

  const rightPane = (
    <SnapshotPane
      entityId={activeEntityId}
      diffData={diffView}
      currentDoc={currentDoc}
      onReady={handleSnapshotReady}
      scrollRef={setRightRef}
      fitOnMount={entityKind === 'canvas'}
    />
  );

  const leftLabel = 'Current version';
  const snapshotLabel = diffView.name
    ? `${diffView.name} — ${formatTimestamp(diffView.timestamp)}`
    : `Snapshot — ${formatTimestamp(diffView.timestamp)}`;
  const rightLabel = isCleanup ? 'AI Suggestion' : snapshotLabel;

  return (
    <Flex vertical className={styles.container}>
      {/* Toolbar */}
      <Flex
        align="center"
        justify="space-between"
        className={styles.toolbar}
      >
        <Flex align="center" gap="sm">
          <Text className={styles.toolbarTitle}>
            {isCleanup ? 'Organise Suggestion' : (diffView.name ?? 'Snapshot')}
          </Text>
          {!isCleanup && (
            <Text size="sm" type="secondary">{formatTimestamp(diffView.timestamp)}</Text>
          )}
        </Flex>
        <Flex align="center" gap="sm">
          {isCleanup ? (
            <>
              <Button size="md" variant="text" onClick={onClose}>Reject</Button>
              <Button size="md" variant="primary" pressEffect="glow" onClick={() => { void handleAcceptOrganize(); }}
                icon={<Icon name="check" size={14} />}
              >
                Accept
              </Button>
            </>
          ) : (
            <>
              <Button size="md" variant="text" onClick={handleBackToList}>All versions</Button>
              <Button size="md" variant="primary" pressEffect="glow" onClick={() => { void handleRestore(); }}
                icon={<Icon name="restore" size={14} />}
              >
                Restore
              </Button>
              <Popconfirm title="Delete this snapshot?" okText="Delete" okDanger onConfirm={() => { void handleDelete(); }}>
                <IconButton
                  size="md"
                  aria-label="Delete snapshot"
                  hoverBackground="color-mix(in srgb, var(--error) 15%, transparent)"
                >
                  <Icon name="delete" size={16} aria-hidden="true" />
                </IconButton>
              </Popconfirm>
              <IconButton size="sm" onClick={onClose} aria-label="Close">
                <Icon name="close" size={16} aria-hidden="true" />
              </IconButton>
            </>
          )}
        </Flex>
      </Flex>

      {/* Side-by-side split — current LEFT, snapshot RIGHT */}
      <Flex vertical className={styles.diffLayout} aria-live="polite">
        <Splitter layout="horizontal" className={styles.splitter}>
          <Splitter.Panel min={120}>
            <Flex vertical className={styles.paneColumn}>
              <PaneLabel>{leftLabel}</PaneLabel>
              {leftPane}
            </Flex>
          </Splitter.Panel>
          <Splitter.Panel min={120}>
            <Flex vertical className={styles.paneColumn}>
              <PaneLabel>{rightLabel}</PaneLabel>
              {rightPane}
            </Flex>
          </Splitter.Panel>
        </Splitter>
      </Flex>
    </Flex>
  );
}

export function DiffViewContainer() {
  const diffView = useUIStore((s) => s.diffView);
  const setDiffView = useUIStore((s) => s.setDiffView);
  return <DiffView diffView={diffView} onClose={() => setDiffView(null)} />;
}
